import { NavLink } from 'react-router-dom'
import { LayoutDashboard, Users, X, Calendar, BarChart3, Sparkles, Video, Briefcase } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import Logo from '../ui/Logo'

const baseNavItems = [
  { to: '/',          icon: LayoutDashboard, label: 'Dashboard',  exact: true,  hideForCeo: true },
  { to: '/calendar',  icon: Calendar,        label: 'Calendario', nvcOnly: true },
  { to: '/analytics', icon: BarChart3,       label: 'Analítica',  nvcOnly: true },
  { to: '/agente',    icon: Sparkles,        label: 'Agente IA',  nvcOnly: true },
  { to: '/reunion',   icon: Video,           label: 'Reunión',    nvcOnly: true },
  { to: '/admin',     icon: Users,           label: 'Usuarios',   adminOnly: true },
]

const ROLE_LABEL = {
  admin: 'Administrador', consultant: 'Consultor', client: 'Cliente',
  caja_user: 'Cliente Caja', client_ceo: 'CEO Cliente',
}

export default function Sidebar({ open, onClose }) {
  const { profile, isAdmin, isNVC, isClientCeo } = useAuth()
  // CEO gets a single "Mi Proyecto" entry pointing at their consultancy dash.
  const navItems = isClientCeo && profile?.consultancy_id
    ? [{ to: `/consultancy/${profile.consultancy_id}/dashboard`, icon: Briefcase, label: 'Mi Proyecto' }]
    : baseNavItems

  return (
    <aside className={`
      fixed inset-y-0 left-0 z-30 w-64
      transform transition-transform duration-300 ease-in-out
      md:static md:z-auto md:translate-x-0
      bg-brand-800 text-cream-100 flex flex-col shadow-xl
      ${open ? 'translate-x-0' : '-translate-x-full'}
    `}>
      {/* Logo */}
      <div className="px-6 py-5 border-b border-brand-700 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Logo variant="mark" className="text-cream-100" size={42} />
          <div>
            <div className="font-display text-lg leading-none text-cream-100 tracking-wide">NEXT VALLEY</div>
            <div className="text-[10px] text-steel-400 uppercase tracking-[0.2em] mt-1">Strategic Partners</div>
          </div>
        </div>
        <button onClick={onClose} className="md:hidden p-1 text-steel-400 hover:text-cream-100 transition-colors">
          <X size={20} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label, exact, adminOnly, nvcOnly, hideForCeo }) => {
          if (adminOnly && !isAdmin) return null
          if (nvcOnly && !isNVC) return null
          if (hideForCeo && isClientCeo) return null
          return (
            <NavLink
              key={to}
              to={to}
              end={exact}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-accent-500/20 text-accent-400 border-l-2 border-accent-500'
                    : 'text-steel-400 hover:bg-white/5 hover:text-cream-100'
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          )
        })}
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-brand-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center text-brand-800 text-sm font-bold flex-shrink-0">
            {profile?.full_name?.charAt(0) ?? '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-cream-100 truncate">{profile?.full_name}</div>
            <div className="text-xs text-steel-400">{ROLE_LABEL[profile?.role] ?? profile?.role}</div>
          </div>
        </div>
      </div>
    </aside>
  )
}
